/* 360 turntable on a grid card — hover to spin, press and hold to scrub.
   Paired with snippets/card-stone-spin.liquid (which explains the zoom maths).

   ── WHY HOVER USED TO SHOW NOTHING, AND WHAT FIXED IT ────────────────────────
   The owner reported that stones with a 360 badge often did not start spinning.
   The cause is in the files themselves, measured 2026-07-30:

     * Stuller's clips are 1.4 – 10.5 MB, and
     * they are NOT faststart — `mdat` is first and `moov` is at the TAIL
       (verified: the first 2 KB of 174623546.mp4 contains `mdat`, no `moov`).

   A browser cannot decode a single frame until it has the `moov` atom. With
   `preload="none"` the first hover therefore had to open the file, discover the
   header was missing, range-request the tail, and only then start — 0.4 s on a
   10 Mbit desk connection for the small clips and ~3.8 s for a 10.5 MB one, far
   worse on mobile data. The old 6 s give-up timer expired first often enough that
   the feature looked broken.

   Two changes fix it:
     1. METADATA IS FETCHED WHEN THE CARD SCROLLS INTO VIEW, not on hover. That is
        the small header round-trip only (`preload="metadata"`), so by the time a
        cursor arrives the `moov` is known and play() is immediate. It is gated to
        `(hover: hover)` devices — a phone has no hover, so it must not pay for it.
     2. THE VIEWER IS REVEALED AS SOON AS ONE FRAME EXISTS (`loadeddata`,
        readyState >= 2), rather than waiting for playback to actually progress.

   ── DRAG ─────────────────────────────────────────────────────────────────────
   Press and hold now scrubs, as on GemsNY. This is only reasonable BECAUSE hover
   plays first: playing warms the buffer, so by the time a finger presses down the
   frames are local and scrubbing is smooth. A cold scrub on a non-faststart file
   would stutter, which is why drag is not offered before the clip has loaded.

   ⚠️ DRAG DIRECTION IS INVERTED RELATIVE TO TIME — the same trap already
   documented in snippets/stone-spin.liquid. The turntable rotates the stone one
   way as the clip advances, so ADDING the drag delta to currentTime makes the
   stone move opposite to the mouse. The owner reported exactly that on the
   product page. `frac` is SUBTRACTED here for the same reason; if it ever feels
   wrong again, flip the sign, not anything else.

   ⚠️ THE POINTER HANDLERS LIVE ON THE CARD'S MEDIA BOX, NOT ON THIS ELEMENT.
   The viewer layer is `pointer-events: none` so the card's <a> stays clickable
   (otherwise you hover a stone, watch it spin, click it and nothing happens).
   Listening on the parent keeps the link working AND gives us the events; a real
   drag then suppresses the click that would otherwise navigate mid-scrub.

   ⚠️ AND POINTER CAPTURE IS TAKEN ONLY ONCE A DRAG IS REAL — NEVER ON pointerdown.
   Capturing retargets the compatibility mouse events to the capture element, and
   the browser fires `click` at that element. The capture host is the media box and
   the product <a> sits INSIDE it, so a captured click lands on an ANCESTOR of the
   link and the event path never crosses it. Measured on a live card 2026-08-02:

     pointerdown -> target <img>,  <a> in path,  defaultPrevented false
     pointerup   -> target .card-product__media-inner,  <a> NOT in path
     click       -> target .card-product__media-inner,  <a> NOT in path, NOT prevented

   Nothing was suppressing the navigation. The link simply never heard the click.
   That is the owner's report — "sometimes it does press in, other times it just
   pauses the video": `onDown` bails out until the clip is armed with a known
   duration, so a press BEFORE the clip loads navigated and a press AFTER it loaded
   did not, which reads as random. Releasing the capture in `onUp` does not undo
   it either; the mouseup target is fixed when the event is dispatched, and the
   old code already released there.

   So capture is deferred to the first move past DRAG_SLOP — and so is the pause,
   which is the other half of the complaint. A plain press now leaves the clip
   spinning and lets the <a> do its job, with cmd-click, middle-click and
   open-in-new-tab all intact because the browser handles the click, not us. */
(function () {
  'use strict';

  var HOVER_DELAY = 90;      // ms the pointer must settle before play()
  // px of travel before a press counts as a drag rather than a click. 10, not the
  // 6 it was: a trackpad tap-to-click drifts several pixels under an ordinary
  // finger, and any drift past this line turns a shopper's click into a scrub —
  // whose click onClick() then deliberately suppresses, so the product page never
  // opens. This is the SECOND way a press failed to navigate; the capture
  // retargeting described in the header is the first.
  var DRAG_SLOP   = 10;
  var STALL_MS    = 12000;   // give-up budget; generous because the files are big
  var current = null;        // the one element allowed to be playing

  var canHover = !(window.matchMedia && window.matchMedia('(hover: none)').matches);
  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  var saveData = !!(conn && (conn.saveData || /^(slow-)?2g$/.test(conn.effectiveType || '')));

  /* ---- MEASURED FIT ---------------------------------------------------------
     The zoom that matches the clip to the still it covers. It is LOOKED UP per
     stone, not computed, and that is the whole point of this block.

     🚨 THE OLD ESTIMATE ASSUMED A CAMERA FIELD THAT DOES NOT EXIST. The Liquid
     snippet still ships `zoom = 0.62 / (length_mm / 21.4)` capped at 1.35, which
     takes Stuller to film every stone at a fixed 21.4 mm field. Measured over 65
     colour stones the implied field runs 14.6-40 mm and length explains only 63%
     of the variance — because there are TWO RIGS in one feed, each framing per
     stone: colour gems on a wide white field, diamonds on a gray macro sweep.
     One shared cap therefore fails in both directions at once:

       colour gems  render at 0.35-0.40 of the tile against a still of 0.84
                    (the owner's "some look really small", 2026-08-05)
       diamonds     render at 0.92-1.14, i.e. corners and tips CUT OFF —
                    13 of 22 sampled were clipped on the live site

     `spin-fit.js` carries `{das id: [fill, safe]}` in thousandths, measured off
     the actual frames by `measure_spin_fit.py`. `fill` is the stone's long edge
     in the face-up frame; `safe` is the largest centre-origin scale that keeps
     every frame of the rotation inside the stage.

     🚨 FIT_TARGET IS 0.76 — stone_image.TARGET_FILL — AND IT WAS BRIEFLY 0.84.
     0.84 came from measuring live stills with `subject_bbox` and reading the
     answer as "the stone plus its drop shadow". It is neither: **the WORDMARK sits
     inside the frame**, so a branded still measures ~0.840 no matter how big its
     stone is. The tell was there and was missed — three different stones measured
     0.840, 0.841, 0.841, which no real subject does. Crop the bottom 12% away and
     all three measure 0.760 exactly, because that is what `frame_native` puts
     there by arithmetic.
     `audit_stone_image_coverage.py` documents this trap verbatim and records that
     it is how "the norm is 0.84" got written down once before. It cost the hover a
     10.5% oversize until 2026-08-06.
     ➡️ The clip carries NO wordmark, so its measured `fill` is the bare stone.
     Match bare stone to bare stone: 0.76. Owner asked for "match the still".

     ⚠️ `safe` WINS WHENEVER THE TWO DISAGREE, and for a large minority it does —
     Stuller filmed those stones close enough that the last of the zoom would take
     an edge off the tile. They stay smaller on purpose.
     🚨 NEVER RAISE FIT_SAFETY TO CLOSE THAT GAP. It is the clipping guard. The
     whole margin costs a few dozen stones a median 2% of size (5% at worst) — the
     ones sitting exactly on their limit — and the alternative is stones losing a
     corner mid-spin. `verify_spin_fit.py` prints the current split both ways. */
  var FIT_TARGET = 0.76;    // = stone_image.TARGET_FILL. Was 0.84 — see above.
  var FIT_SAFETY = 0.95;     // 16- vs 32-frame sampling moved `safe` by <=0.5%
  var FIT_MIN = 0.4, FIT_MAX = 4.0;
  // The value `stone-hover-spin.css` falls back to in `scale(var(--shs-zoom, 1))`.
  // ⚠️ IT LIVES IN TWO FILES AND verify_spin_fit.py PINS THEM TOGETHER — if the
  // stylesheet's fallback ever changes and this does not, every card that relies on
  // the fallback gets a stop for a zoom it is not rendering at.
  var ZOOM_CSS_FALLBACK = 1;

  var fitMap = window.SR_SPIN_FIT || null;
  var fitPending = [];      // <stone-hover-spin> elements awaiting the map
  var fitReady = [];        // outside callers awaiting it (see window.SRSpinFit)
  var fitAsked = false;

  function dasOf(src) {
    var m = /\/([^\/?#]+)\.mp4(?:[?#]|$)/i.exec(src || '');
    return m ? m[1] : '';
  }

  function fitZoom(src) {
    var e = fitMap && fitMap[dasOf(src)];
    if (!e || !(e[0] > 0)) return 0;
    var z = Math.min(FIT_TARGET / (e[0] / 1000), (e[1] / 1000) * FIT_SAFETY);
    return Math.max(FIT_MIN, Math.min(FIT_MAX, z));
  }

  // `safe` in map units, or 0 when this clip has not been measured.
  function fitSafe(src) {
    var e = fitMap && fitMap[dasOf(src)];
    return e && e[1] > 0 ? e[1] / 1000 : 0;
  }

  /* THE CLIP'S OWN BACKDROP, packed 0xRRGGBB by measure_spin_fit.border_plate().
     ⚠️ OPTIONAL BY DESIGN — an entry measured before 2026-08-12 has only two
     elements, and those cards fall back to sampling our own still (ensurePlate),
     which is exactly today's behaviour. `0` is a legitimate colour (black), so the
     test is on LENGTH, never on truthiness of the value. */
  function rgbOf(v) {
    return 'rgb(' + ((v >> 16) & 255) + ',' + ((v >> 8) & 255) + ',' + (v & 255) + ')';
  }
  function fitPlate(src) {
    var e = fitMap && fitMap[dasOf(src)];
    if (!e || e.length < 3) return '';
    return rgbOf(e[2]);
  }
  /* THE BOTTOM EDGE, packed the same way as a 4th element (2026-09-02).
     Stuller's gray sweep is lit as a VERTICAL GRADIENT — the bottom edge reads up
     to 14 luminance levels brighter than the top (das 150820207: 193 against 207)
     — and the plate is only ever seen in the bands ABOVE and BELOW the clip. One
     flat colour is therefore ~7 levels off at BOTH seams; painting the top band
     the top edge's colour and the bottom band the bottom's halves the residual
     (p90 4.0 -> 2.0 levels over 30 clips, measure_spin_fit.edge_plates). When only
     the flat plate is known, `[2]` IS the plate and this returns '' so the caller
     paints it flat — today's behaviour, byte for byte. */
  function fitPlateBottom(src) {
    var e = fitMap && fitMap[dasOf(src)];
    if (!e || e.length < 4) return '';
    return rgbOf(e[3]);
  }

  /* The zoom in force on an element -- READ, never recomputed. The card carries it
     as `--shs-zoom` and the product page as `--spin-zoom`, both set inline (by
     Liquid, by diamond-browse.js, or by applyFit), so a computed read returns
     whichever of the estimate and the measurement is actually APPLIED.
     ⚠️ getComputedStyle FORCES A STYLE RECALC, so a read immediately after a
     setProperty in the same task sees the new value — which is what lets applyFit
     write the zoom and then call applyFeather without threading it through.

     🚨 AN UNSET PROPERTY IS NOT AN UNKNOWN ZOOM — IT IS THE CSS FALLBACK, AND
     READING IT AS "unknown" WAS A REGRESSION (Codex, 2026-09-03). The stylesheet
     says `transform: scale(var(--shs-zoom, 1))`, so an element that never had the
     property IS rendering at exactly 1. Two live builders create the card that way
     and never set it — `ring-builder-stones.js` (builder step 2b) and
     `stone-list.js` (the compare tray) — so on those grids a clip hovered before
     the map landed got py:null, no `--shs-py` written, and the gradient sat on its
     0% fallback for the life of the card. Measured: the correct stop for a 721x586
     clip at zoom 1 is 9.362%, and it was rendering at 0%.
     ⚠️ THIS IS NOT THE `zoom` DEFAULT featherFor REFUSES, AND THE DIFFERENCE IS THE
     WHOLE POINT. A caller that omits featherFor's argument has told us nothing about
     the element, so guessing 1 there would place a wrong stop on a card at 1.3. An
     unset PROPERTY is positive evidence about this element: the fallback is in
     force. One is guessing; the other is reading.
     ⚠️ 0 IS KEPT FOR "CANNOT READ AT ALL" — a detached element has no computed
     style, and there the stop genuinely is unknowable. */
  function appliedZoom(el, prop) {
    if (!el || !el.isConnected) return 0;          // unreadable — genuinely unknown
    var v = parseFloat(getComputedStyle(el).getPropertyValue(prop));
    return v > 0 ? v : ZOOM_CSS_FALLBACK;          // unset -> what the CSS renders
  }

  /* THE FEATHER GEOMETRY, IN ONE PLACE (2026-09-02). It used to live inside the
     card element's applyFeather(); the product page (`snippets/stone-spin.liquid`)
     now draws the same plate and the same feather on its own stage, and a second
     copy of this arithmetic is exactly how that page and this card drifted apart
     on the ZOOM (see the SRSpinFit comment below). Both consumers call this.

     `box` is the element's own PRE-TRANSFORM width in CSS px: `contain` maps the
     clip's long side to `box`, and the display zoom scales the element afterwards,
     so it cancels and must not appear in the mask's numbers. Returns
       fx, fy  where `contain` starts painting, as a % of the ELEMENT box — the
               mask's stops, because the mask rides on the transformed element
       px, py  the same edge as a % of the STAGE, i.e. after the zoom — the plate
               gradient's stops, because the plate is painted on the untransformed
               box behind the clip. A zoom above 1 pushes the edge past the stage
               and the value clamps at 0; below 1 the band is wider than fy says.
       fe      the feather width in px (0 = the stone reaches its own frame edge)
     or null when the clip is unmeasured or its metadata is not in yet. fx/fy/px/py
     are still returned when fe is 0, so the plate can land on the real seam.

     🚨 `zoom` IS THE ZOOM ACTUALLY ON THE ELEMENT AND IS PASSED IN, NEVER RE-DERIVED
     HERE (2026-09-03). This function used to read `fitZoom(src)` itself, which is the
     zoom the map SAYS -- and `applyFit` deliberately refuses to move a playing clip
     onto that value. So for exactly the shopper the guard protects (hovered before
     the map landed) the video sat at the Liquid fallback while px/py were placed for
     the measured one, and the seam this whole mechanism exists to hide came back.
     Codex found it, 2026-09-03; its own replay put it at 1,225 of 11,002 clips with a
     stop error >= 0.5 percentage points, worst 9.71. It is the MIRROR IMAGE of the
     2026-09-02 fix: guarding the zoom against a late map while letting the gradient
     use the new measurement means the two are computed from different zooms.
     ⚠️ AN UNKNOWN ZOOM RETURNS px/py AS null, IT DOES NOT GUESS. fx/fy/fe do not
     depend on the zoom at all -- they ride on the transformed element -- so the MASK
     is always correct and only the plate's stop is withheld. A caller that omits the
     argument therefore loses the stop (the gradient falls back to spanning the whole
     box, i.e. the pre-2026-09-02 behaviour) and never places a wrong one. Defaulting
     to `fitZoom(src)` here is what the bug WAS; defaulting to 1 would be the same
     mistake with a different number.

     🚨 THE MARGIN IS PER AXIS, AND `safe` ONLY GIVES THE BINDING ONE.
     measure_spin_fit normalises the stone's half-extent to the clip's LONG side and
     keeps `max(rx, ry)`, so 0.5/safe is an upper bound on the stone in EITHER
     direction — but the FRAME's half-extent is not 0.5 in either direction.
     `object-fit: contain` gives a 1.23:1 clip only 1/1.23 of the box vertically, so
     a stone that is tight top-to-bottom has a vertical margin of nearly zero while
     the horizontal one still measures 9% of the box. Sizing off the long axis alone
     said "28 px clear" for exactly that case and would have feathered straight
     through the stone. Only half the measured margin is ever spent, capped at 6px. */
  function featherFor(src, vw, vh, box, zoom) {
    if (!vw || !vh || !box) return null;           // metadata not in yet / no layout
    var safe = fitSafe(src);
    if (!safe) return null;                        // unmeasured — caller keeps its own
    var s = Math.min(box / vw, box / vh);          // object-fit: contain
    var halfW = vw * s / 2, halfH = vh * s / 2;    // the frame's half-extents
    var fe = 0;
    if (safe > 1) {
      var stone = (0.5 / safe) * box;              // the stone's, upper bound
      fe = Math.max(0, Math.min(6, Math.min(halfW - stone, halfH - stone) * 0.5));
    }
    var fx = (box - vw * s) / 2 / box * 100;       // where `contain` starts painting
    var fy = (box - vh * s) / 2 / box * 100;
    var z = (typeof zoom === 'number' && zoom > 0) ? zoom : 0;   // APPLIED, or unknown
    return {
      fx: fx, fy: fy,                              // the mask — zoom-independent
      px: z ? Math.max(0, 50 - (50 - fx) * z) : null,   // the same edge, after the zoom
      py: z ? Math.max(0, 50 - (50 - fy) * z) : null,
      fe: fe
    };
  }

  // ⚠️ NEVER RESIZE A CLIP THE SHOPPER IS ALREADY WATCHING. If the lookup lands
  // late it must be dropped, not applied: the transform has no transition, so a
  // late write is a hard snap mid-spin. The card keeps the Liquid estimate for
  // that view and gets the measured one on the next. In practice the race is not
  // close — the map is a small cached file and the clip is 1.4-10.5 MB.
  function applyFit(el) {
    /* 🚨 ONLY THE ZOOM IS GUARDED ON `is-playing` (2026-09-02). It used to be the
       whole function — and that was the "sometimes" on the grid: the map is fetched
       on the first card that upgrades, so a shopper who hovers BEFORE it lands gets
       a reveal with the still-sampled (or transparent) plate, and when the map then
       arrived this returned at the top, skipping the measured plate AND the feather
       for that card for the life of the page. Nothing re-asked: reveal() only
       samples the still, and start() only re-runs the feather. So the same diamond
       showed a box on the first hover and was clean after a reload — a race, which
       reads as intermittent. The zoom must still not move under a playing clip
       (a hard snap, no transition); the plate and the mask are read live by the
       CSS and can only correct a wrong edge, so they apply whenever they arrive.
       🚨 AND THE PLATE'S STOP MUST FOLLOW THE ZOOM THIS BRANCH DECIDED, NOT THE
       MAP'S (2026-09-03). Skipping the write here and then letting featherFor
       re-derive `fitZoom(src)` put the video at one zoom and the gradient stop at
       another — for precisely the shopper this guard exists to protect. There is one
       decision point now and `applyFeather` reads the value off the ELEMENT, so the
       two cannot disagree however many call sites there are. */
    if (!el.classList.contains('is-playing')) {
      var z = fitZoom(el.src);
      if (z) el.style.setProperty('--shs-zoom', z.toFixed(4));
    }
    /* 🚨 THE PLATE IS SET HERE, BEFORE ANY HOVER, AND THAT IS THE POINT.
       It used to be sampled inside reveal(), i.e. at the moment the clip was
       already fading in — and on every JS-built card that sample needs an async
       CORS probe, because the still is on cdn.shopify.com. Measured on the live
       diamond grid 2026-08-12: `is-playing` at t=0, `--shs-plate` at t=98 ms,
       against a 220 ms fade. So for the first half of every fade the letterbox
       band showed the STILL — and the still's bottom margin is where the SAPPHIRE
       ROW wordmark is stamped. That is the owner's "at first you can still see the
       Sapphire Row wordmark as it starts spinning, then it disappears".
       ⚠️ The colour tab does not show it and never did, which is what made it look
       like a diamond-only defect: the first page of colour cards is rendered in
       LIQUID, so `image_url` emits a same-origin `//thesapphirerow.com/cdn/…` URL
       and the sample succeeds synchronously. Same code, same element, no probe. */
    var p = fitPlate(el.src);
    if (p) {
      el.style.setProperty('--shs-plate', p);
      // The bottom edge, or the same flat colour again for a clip measured before
      // the pair existed — the gradient then collapses to the flat plate.
      el.style.setProperty('--shs-plate-b', fitPlateBottom(el.src) || p);
      el._plateDone = true;          // nothing to sample; the map already knows
    }
    el.applyFeather();
  }

  // Fetched on the first card that needs it, never on pages without stones.
  // `theme.liquid` publishes the versioned asset URL; without it the map simply
  // never arrives and every card keeps the Liquid estimate — the designed
  // fallback, and also what a brand-new stone gets until the next measure run.
  /* 🚨 PHONE CARDS FETCH IT AFTER THE PAGE HAS LOADED (2026-10-13, Clarity: phone speed). On a phone a card plays
     nothing until its 360 badge is tapped, yet the ~300 KB table was fetched the moment the first card appeared:
     measured on /collections/all-gemstones at 390 px on slow 4G it downloaded from 4.2 s to 13.4 s, over the banner
     (the main image, done at 9.7 s). So a card on a device that cannot hover waits for `load` and an idle moment,
     then takes the same path as before; a tap that comes first uses the fallback an unmeasured stone already gets.
     `eager` (SRSpinFit.ready(), the product page) never waits: that video AUTOPLAYS before `load` and must not
     play at the estimate and then snap (Codex review-mv6lg56f-njt7cx). Desktop: unchanged. */
  var fitWaiting = false;
  function loadFit(eager) {
    if (fitAsked || fitMap || (fitWaiting && !eager)) return;
    if (!eager && !canHover && document.readyState !== 'complete') {
      fitWaiting = true;
      var later = function () {
        var go = function () { fitWaiting = false; loadFit(); };
        if (window.requestIdleCallback) window.requestIdleCallback(go, { timeout: 3000 }); else setTimeout(go, 1000);
      };
      window.addEventListener('load', later, { once: true });
      return;
    }
    fitAsked = true;
    var url = window.SR_SPIN_FIT_URL;
    // No URL means the layout tag is missing. Settle the map to empty rather than
    // just returning, so the next card takes the fast path instead of queueing
    // into a `fitPending` that nothing will ever drain — on a 48-card grid that
    // is 48 dead references held for the life of the page.
    if (!url) {
      fitMap = {};
      fitPending.length = 0;
      fitReady.splice(0).forEach(function (cb) { cb(); });
      return;
    }
    var s = document.createElement('script');
    s.src = url;
    s.async = true;
    var settle = function () {
      fitMap = window.SR_SPIN_FIT || {};
      fitPending.splice(0).forEach(applyFit);
      fitReady.splice(0).forEach(function (cb) { cb(); });
    };
    s.onload = settle;
    // A failed fetch must settle too, or `ready` callers wait forever and the
    // product page never falls back to its Liquid estimate.
    s.onerror = settle;
    document.head.appendChild(s);
  }

  /* 🚨 PUBLISHED SO THE PRODUCT PAGE CANNOT DRIFT FROM THIS ONE.
     `snippets/stone-spin.liquid` is a DIFFERENT custom element (`<stone-spin>`,
     `--spin-zoom`) on a different stage, and it carried its own copy of the same
     length-based estimate. That is not hypothetical drift: its own header records
     the last time it happened — the card's cap was lowered to 1.35 when stones
     started leaving the frame and that file "WAS NOT [lowered], so the same stone
     rendered clean in the card preview and clipped on its own product page."
     Confirmed live 2026-08-05, 4 of 5 sampled diamond product pages clipping, two
     of them drawing the stone past the stage outright (1.04 and 1.10 of it).
     So the constants and the composition live in exactly one place and the product
     page calls in. Do not re-implement `zoomFor` anywhere; `verify_spin_fit.py`
     fails the build if a second copy of the arithmetic appears. */
  window.SRSpinFit = {
    // The measured zoom for a clip URL, or 0 when that stone has not been
    // measured yet — callers keep their own fallback in that case.
    zoomFor: function (src) { return fitZoom(src); },
    // The clip's own backdrop as {top, bottom} CSS colours, or null for a clip
    // whose plate was never measured — callers keep their own plate then. `bottom`
    // repeats `top` when only the flat plate is known (measured before 2026-09-02).
    plateFor: function (src) {
      var t = fitPlate(src);
      return t ? { top: t, bottom: fitPlateBottom(src) || t } : null;
    },
    // {fx, fy, fe} for a clip drawn `object-fit: contain` in a square box of `box`
    // pre-transform CSS px — see featherFor() above. null when unmeasured or when
    // the metadata is not in yet; the caller re-asks on `loadedmetadata`.
    featherFor: function (src, vw, vh, box, zoom) {
      return featherFor(src, vw, vh, box, zoom);
    },
    // The zoom in force on an element, for a consumer whose variable is named
    // differently (the product page uses `--spin-zoom`). Exported rather than
    // re-implemented for the same reason featherFor is: two copies of "which zoom
    // is applied" is the defect this pair was just fixed for.
    appliedZoom: function (el, prop) { return appliedZoom(el, prop); },
    // Runs `cb` once the map is available (immediately if it already is). The
    // map is fetched on first use, so an early caller must wait rather than
    // conclude the stone is unmeasured.
    ready: function (cb) {
      if (fitMap) { cb(); return; }
      fitReady.push(cb);
      loadFit(true);                  // eager: the product page's video autoplays (see loadFit)
    }
  };

  /* 🚨 THE HANDSHAKE IS A QUEUE, BECAUSE THIS FILE ARRIVES TOO LATE TO BE ASKED.
     This asset is DEFERRED from theme.liquid, so it runs after the document is
     parsed. The product page's `<stone-spin>` is defined by an INLINE script in
     `snippets/stone-spin.liquid`, and `customElements.define` upgrades matching
     elements synchronously — so its connectedCallback fires DURING parsing, well
     before `window.SRSpinFit` exists.
     A plain `if (window.SRSpinFit)` guard therefore does nothing at all, and does
     it silently: measured on the live storefront 2026-08-05, the API was present,
     the map held 10,858 entries, `zoomFor()` returned the right answer when called
     by hand — and every product page still rendered at the Liquid estimate,
     1.35 on a stone whose safe limit is 1.00.
     So callers PUSH a function and this drains whatever queued before it loaded,
     then leaves a pass-through behind for anything that upgrades later. Works
     whichever order the two scripts happen to run in. */
  var queued = window.SRSpinFitQueue;
  window.SRSpinFitQueue = {
    push: function (fn) { window.SRSpinFit.ready(fn); }
  };
  if (queued && queued.length) {
    queued.forEach(function (fn) { window.SRSpinFit.ready(fn); });
  }

  // Warm only what is on screen, and only where a cursor can exist.
  var warmer = null;
  if (canHover && !saveData && 'IntersectionObserver' in window) {
    warmer = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.warm();
          warmer.unobserve(e.target);
        }
      });
    }, { rootMargin: '200px 0px' });
  }

  // Mobile home cards request the shared fit map shortly before they enter view.
  // Product-page ready() callers and every other page keep their existing path.
  var fitObserver = null;
  if (document.body.classList.contains('template-index') && window.matchMedia &&
      window.matchMedia('(max-width: 749px)').matches &&
      'IntersectionObserver' in window && !(window.Shopify && Shopify.designMode)) {
    fitObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) entry.target.requestFit();
      });
    }, { rootMargin: '800px 0px' });
  }

  function stopCurrent(except) {
    if (current && current !== except) { current.stop(); current = null; }
  }

  customElements.get('stone-hover-spin') || customElements.define('stone-hover-spin',
    class extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('.shs__v');
        this.badge = this.querySelector('.shs__badge');
        this.src = this.dataset.src || '';
        this.armed = false;
        this.warmed = false;
        // "the shopper is on this card RIGHT NOW" — see reveal()
        this.wanted = false;
        this.dragging = false;
        this.moved = false;
        if (!this.video || !this.src) return;

        // Measured zoom in place of the Liquid estimate — see MEASURED FIT above.
        // EVERY surface picks this up: collection and search cards build the
        // element in Liquid, diamond-browse.js and stone-list.js build it in JS,
        // and all of them arrive here because the upgrade happens on insert. That
        // is why the fix lives in the custom element and not in the snippet.
        if (fitMap) applyFit(this);
        else if (fitObserver) fitObserver.observe(this);
        else this.requestFit();

        // A dead URL must not advertise itself.
        this.video.addEventListener('error', () => this.disable());

        // The feather needs the clip's ASPECT, which only the file knows. Warming
        // fetches the header long before any hover, so in practice this has landed
        // by the time it matters; applyFit() also calls it, for whichever of the
        // two arrives second.
        this.video.addEventListener('loadedmetadata', () => this.applyFeather());

        // Reveal on the FIRST AVAILABLE FRAME. `loadeddata` fires at readyState 2,
        // well before `playing`, and it is the earliest moment there is anything
        // real to show. `playing` and a progressing `timeupdate` are kept as
        // belt-and-braces for engines that skip straight past loadeddata.
        var reveal = () => this.reveal();
        this.video.addEventListener('loadeddata', reveal);
        this.video.addEventListener('playing', reveal);
        this.video.addEventListener('timeupdate', () => {
          if (this.video.currentTime > 0) this.reveal();
        });

        // ---- pointer handling on the CARD, not on this layer (see header) ----
        this.host = this.closest('.card-product__media-inner') || this.parentElement;
        if (this.host) {
          this.host.addEventListener('pointerenter', this.onEnter);
          this.host.addEventListener('pointerleave', this.onLeave);
          this.host.addEventListener('pointerdown', this.onDown);
          this.host.addEventListener('pointermove', this.onMove);
          this.host.addEventListener('pointerup', this.onUp);
          this.host.addEventListener('pointercancel', this.onUp);
          // Capture phase: the click must be killed before the <a> sees it.
          this.host.addEventListener('click', this.onClick, true);
        }

        this.badge.addEventListener('click', this.onBadge);
        this.badge.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault(); e.stopPropagation(); this.onBadge(e);
          }
        });

        if (warmer) warmer.observe(this);
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        clearTimeout(this.stallTimer);
        if (warmer) warmer.unobserve(this);
        if (fitObserver) fitObserver.unobserve(this);
        var pendingIndex = fitPending.indexOf(this);
        if (pendingIndex !== -1) fitPending.splice(pendingIndex, 1);
        if (current === this) current = null;
      }

      disable() {
        this.classList.remove('is-loading', 'is-playing');
        this.classList.add('is-dead');
        if (this.badge) this.badge.hidden = true;
      }

      requestFit() {
        if (fitObserver) fitObserver.unobserve(this);
        if (fitMap) applyFit(this);
        else {
          if (fitPending.indexOf(this) === -1) fitPending.push(this);
          loadFit();
        }
      }

      // Fetch the header only. This is what makes a later play() instant.
      warm() {
        if (fitObserver) this.requestFit();
        if (this.warmed || this.armed) return;
        this.warmed = true;
        this.video.preload = 'metadata';
        this.video.src = this.src;
      }

      arm() {
        if (fitObserver) this.requestFit();
        if (this.armed) return;
        this.armed = true;
        this.video.preload = 'auto';
        if (!this.video.src) this.video.src = this.src;
      }

      // ⚠️ WARMING MUST NEVER REVEAL. THIS GUARD IS THE WHOLE FEATURE.
      // `warm()` sets `src` when the card scrolls into view, and setting a src is
      // enough to fire `loadeddata` — so a card that had never been hovered called
      // reveal(), took `is-playing`, and faded its VIDEO FRAME in over the branded
      // still. The owner reported it exactly: "I saw the pictures for a second and
      // then the video preview."
      //
      // It is not a subtle difference on screen. Stuller's turntable leaves the
      // stone filling ~37% of the frame while our card still is normalised to 76%
      // (stone_image.TARGET_FILL), so a revealed video shows a small stone on a
      // white plate where the card should show a large branded one — and the
      // wordmark disappears with it.
      //
      // ⚠️ AND THE FLAG IS `wanted`, NOT `armed`. `armed` is sticky for the life of
      // the element — it goes true on the first hover and never comes back down —
      // so gating on it fixes the scroll-in reveal and leaves a second one: after
      // the cursor LEAVES, stop() removes `is-playing`, a trailing `timeupdate`
      // from the just-paused video calls reveal(), and the class goes straight back
      // on. Measured: the video was still showing after pointerleave. `wanted` is
      // set by start() and cleared by stop()/giveUp(), so a late event that arrives
      // after the shopper has moved on cannot resurrect the viewer.
      /* THE PLATE UNDER THE LETTERBOX (owner, 2026-08-06).
         Stuller's turntable is 721x586 — NOT square — so `object-fit: contain` in a
         square tile letterboxes it ~9.4% top and bottom at zoom 1, and more as the
         measured zoom drops (12.8% on das 158192597, measured on the live card).
         The layer was `background: transparent` so that band showed the STILL — and
         the still's bottom margin is where the SAPPHIRE ROW wordmark is stamped, so
         every diamond card flashed its own wordmark on hover with the clip floating
         above it. That is the owner's "it places the 360 over the still and you can
         see that".

         Transparent was itself the fix for the version before it (`background: #fff`
         painted WHITE bars over a grey still, 2026-08-05), so the plate must not go
         back to a hardcoded colour — diamonds ship on Stuller's own card, which is
         180-255 and different tile to tile.

         🚨 EVERYTHING BELOW IS NOW THE FALLBACK PATH. Sampling the STILL is
         measuring the WRONG SIDE OF THE SEAM: the plate does not meet our image, it
         meets STULLER'S backdrop, along a straight ~300 px edge. Measured over 18
         diamonds the step across it ran a median of 8.4 luminance levels, p90 15.4
         and max 52.7 — the owner's "it's clearly in a box", 2026-08-12. The plate is
         now MEASURED FROM THE CLIP by measure_spin_fit.border_plate() and carried in
         spin-fit.js, so applyFit() sets it before the first hover. This runs only
         for a clip measured before that existed.

         So it is SAMPLED, at run time, from the still this layer is covering.
         Nothing is stored, nothing is baked into a shard, and there is no second
         copy of the value to drift from the image — the pixel IS the image.
         ⚠️ Falls back to transparent on any failure, which is exactly today's
         behaviour, so a card can never end up worse than it is now.

         🚨 AND THE SAMPLE FAILED ON EVERY CARD BUILT IN JS, WHICH IS MOST OF THEM.
         This block used to say the card image "is served from the storefront's own
         /cdn path, so the canvas is same-origin ... (verified on the live site)".
         That was verified on a LIQUID card and is true only there: `image_url`
         emits `//thesapphirerow.com/cdn/shop/files/…`. Every card built in
         JavaScript — the all-stones browser, the list view, the three builder
         grids — takes its URL from the SHARD, which bakes the Admin API's
         `featuredMedia.image.url`, and that is always `https://cdn.shopify.com/s/…`.
         Different origin, so `getImageData` throws SecurityError, `--shs-plate` is
         never set, and the letterbox falls straight back to `transparent` — the
         exact bug this whole block exists to fix, alive on the surfaces that render
         the most cards. The owner found it on the beryls, 2026-08-10: those five
         clips are 720x586 (the WIDE rig, on COLOURED stones — see below) and the
         band ran 1.5% to 10.6% of tile height with the wordmark sitting in it.
         ⚠️ It is invisible from the Liquid side. Hovering a card on
         /collections/sapphires shows the plate working perfectly, on the same
         stone, in the same element, with the same code.

         So a tainted read RETRIES through a CORS-mode probe of the SAME url —
         `crossOrigin='anonymous'` on a fresh Image, width knocked down to 40px.
         Shopify's CDN answers with `access-control-allow-origin: *` (verified), so
         that canvas is clean. Two properties matter and both are deliberate:
           * IT NEVER TOUCHES THE VISIBLE <img>. Setting crossOrigin on the card's
             own image would make it a CORS fetch, and a CORS fetch that is refused
             does not fall back — it renders nothing. The card the shopper paid for
             is not the thing to gamble.
           * IT IS THE SAME URL, only re-sized. Rewriting cdn.shopify.com into the
             storefront path would work today (measured) and is one CDN
             reorganisation away from 404ing every tile; nothing here needs to know
             how one maps to the other.
         The probe is ~1 KB and runs only for a card the shopper actually hovers,
         never for the grid. On any failure the var stays unset — transparent, i.e.
         today's behaviour. */
      /* ---- THE SEAM, AND WHY A FLAT PLATE IS NOT ENOUGH ---------------------
         73.8% of diamond clips do not cover the tile (against 5.6% of colour
         clips — measured over the whole map, 2026-08-12), because diamonds are
         filmed MACRO and their measured zoom lands near 1.0 while colour stones
         are filmed small and get zoomed past coverage. So on three diamonds in
         four there is a real edge where Stuller's backdrop meets our plate, and
         on almost no colour stone is there one at all. That, not the plate
         colour, is why the owner sees this on diamonds and not on colour.

         🚨 AND STULLER'S SWEEP IS VIGNETTED, so no single flat colour matches it
         all the way round: measured over 14 clips the best achievable residual is
         7-11 luminance levels whichever estimator you pick (median / mean /
         trimmed midrange all within ~1 level of each other). Across a straight
         300 px edge that still reads as a box. Rendered side by side, the plate
         alone leaves a visible rectangle on 3 of 5 of the worst cases and the
         feather removes it on all 5.
         ⚠️ THE FEATHER ALONE IS NOT ENOUGH EITHER, and it is worse than it looks:
         with today's still-sampled plate it turns the 52-level case into a soft
         white HALO rather than a hard box. Both, or neither.

         ⚠️ IT IS SIZED FROM `safe`, SO IT CANNOT EAT A STONE. `safe` is defined as
         the scale at which the stone's widest rotation touches the stage edge, so
         the stone's half-extent in frame units is 0.5/safe and the clear margin to
         the frame edge is 0.5 - 0.5/safe of the long side. A clip with safe <= 1
         has NO margin — the stone reaches its own frame edge — and gets no feather
         at all. Only half the measured margin is ever used.

         ⚠️ AND THE MASK MUST START AT THE CONTENT EDGE, NOT THE ELEMENT EDGE.
         `object-fit: contain` letterboxes the clip INSIDE an element that is still
         the full tile, so the painted edge is inset by (1 - 1/ar)/2 on the short
         axis. Feathering the element box would fade transparent bars and leave the
         real seam untouched — a fix that changes nothing and looks like it works. */
      applyFeather() {
        // The arithmetic is featherFor() above — shared with the product page, so
        // the two cannot drift. This method only writes the result onto the element.
        // ⚠️ THE ZOOM COMES OFF THE ELEMENT, NOT OUT OF THE MAP. applyFit may have
        // just refused to move it (a playing clip), so the map's value and the
        // element's differ for exactly the case that matters — see featherFor.
        var f = featherFor(this.src, this.video.videoWidth, this.video.videoHeight,
                           this.getBoundingClientRect().width,
                           appliedZoom(this, '--shs-zoom'));
        if (!f) { this.removeAttribute('data-feather'); this.style.removeProperty('--shs-fe'); return; }
        // The letterbox offsets are written whenever the geometry is known — the
        // plate gradient reads them too, so its two colours land exactly on the
        // clip's top and bottom edges rather than on the box's.
        this.style.setProperty('--shs-fx', f.fx.toFixed(3) + '%');
        this.style.setProperty('--shs-fy', f.fy.toFixed(3) + '%');
        // null = the applied zoom is unknown, so there is no honest stop to write.
        // The gradient then spans the whole box, which is what it did before the
        // stop existed — wrong-ish and stable, never confidently misplaced.
        if (f.py !== null) this.style.setProperty('--shs-py', f.py.toFixed(3) + '%');
        /* 🚨 CLEAR THE OLD MASK, DO NOT JUST DECLINE TO SET A NEW ONE (Codex,
           2026-09-04). This runs again whenever the tile changes size — the browse
           grid's density buttons and a viewport resize both re-run it — and a box
           that HAD a safe margin at 600 px has none at 150 px. Returning without
           removing the previous `data-feather` / `--shs-fe` left the old, now-unsafe
           mask fading real stone at the new size. `snippets/stone-spin.liquid` (the
           product-page copy of this same arithmetic) has always cleaned up here; this
           copy did not, which is the one-line divergence between two copies of one
           rule that this repo keeps paying for. */
        if (f.fe < 1) {
          this.removeAttribute('data-feather');
          this.style.removeProperty('--shs-fe');
          return;                                     // no safe margin — leave it hard
        }
        this.style.setProperty('--shs-fe', f.fe.toFixed(1) + 'px');
        this.setAttribute('data-feather', '');
      }

      stillImg() {
        var box = this.closest('.card-product__media-inner') || this.parentElement;
        return box && box.querySelector('img');
      }

      // null means "no colour", for BOTH reasons — tainted canvas and a genuinely
      // transparent pixel. ensurePlate() probes on either; a transparent PNG simply
      // probes to transparent again and costs one 1 KB request.
      sampleFrom(img) {
        try {
          var c = document.createElement('canvas');
          c.width = c.height = 1;
          var g = c.getContext('2d');
          // Top-left corner of the still: outside the stone (TARGET_FILL 0.76
          // centred leaves ~12% of backdrop) and away from the wordmark, which
          // sits bottom-right. One averaged pixel is all this needs.
          g.drawImage(img, 0, 0, 2, 2, 0, 0, 1, 1);
          var p = g.getImageData(0, 0, 1, 1).data;
          if (p[3] < 8) return null;                       // fully transparent
          return 'rgb(' + p[0] + ',' + p[1] + ',' + p[2] + ')';
        } catch (e) {
          return null;                                     // tainted or blocked
        }
      }

      /* The tiny CORS twin of a still whose pixels we could not read directly.
         ⚠️ `crop` AND `height` ARE DROPPED, NOT OVERRIDDEN. Shopify's `crop` needs
         both dimensions, so carrying it across with only `width` set is asking for a
         transform that does not mean what it says. Dropped, the probe is the whole
         master scaled to 40px wide — and its top-left pixel is the master's
         top-left, which is the backdrop this needs, whatever the card happened to
         crop. 40px is small enough that 2 source pixels still sit well inside the
         ~12% margin (measured on the beryls: uniform 255,255,255 across the band,
         with the wordmark the only thing that is not). */
      probeUrl(src) {
        try {
          var u = new URL(src, location.href);
          u.searchParams.delete('crop');
          u.searchParams.delete('height');
          u.searchParams.set('width', '40');
          return u.toString();
        } catch (e) {
          return '';
        }
      }

      plateProbe(img) {
        if (this._probed) return;
        var url = this.probeUrl(img.currentSrc || img.src);
        if (!url) return;
        this._probed = true;
        var self = this;
        var p = new Image();
        // ⚠️ BEFORE `src`. Setting crossOrigin after the request has started is
        // ignored, and the canvas is tainted again with nothing to show for it.
        p.crossOrigin = 'anonymous';
        p.onload = function () {
          var c = self.sampleFrom(p);
          if (c) self.style.setProperty('--shs-plate', c);
        };
        // No CORS headers, or a dead URL: leave the var unset. The card keeps the
        // transparent plate it has today — this can only ever add.
        p.onerror = function () {};
        p.src = url;
      }

      /* ⚠️ THE STILL IS LAZY, SO "NOT READY YET" IS THE NORMAL CASE, NOT AN EDGE.
         The card image ships `loading="lazy"` with class `lazy-image img-loading`
         and sits at opacity 0 until it decodes, so `complete` is false and
         `naturalWidth` is 0 for a card the shopper can already hover — reveal()
         can genuinely land first. Sampling once at reveal and giving up would
         leave exactly the cards a fast shopper hits with no plate at all.
         The CSS reads `var(--shs-plate)` live, so setting it LATE still lands:
         if the still has not decoded, wait for its load and set it then, mid-spin.
         One listener per element, and it is never re-armed. */
      ensurePlate() {
        if (this._plateDone) return;
        var img = this.stillImg();
        if (!img) { this._plateDone = true; return; }
        var self = this;
        var apply = function () {
          if (self._plateDone) return;
          if (!img.complete || !img.naturalWidth) return;
          self._plateDone = true;
          var c = self.sampleFrom(img);
          if (c) { self.style.setProperty('--shs-plate', c); return; }
          // Unreadable — on a JS-built card that is the normal case, not an edge
          // (the still is on cdn.shopify.com). One 1 KB CORS probe settles it, and
          // lands late the same way this whole method does: the CSS reads
          // `var(--shs-plate)` live, so a plate that arrives mid-spin still paints.
          self.plateProbe(img);
        };
        apply();
        if (!this._plateDone && !this._plateWaiting) {
          this._plateWaiting = true;
          img.addEventListener('load', apply, { once: true });
        }
      }

      reveal() {
        if (!this.wanted) return;
        clearTimeout(this.stallTimer);
        this.ensurePlate();
        this.classList.add('is-playing');
        this.classList.remove('is-loading');
      }

      giveUp() {
        this.wanted = false;
        this.classList.remove('is-loading');
        if (this.armed && !this.video.paused) this.video.pause();
        if (current === this) current = null;
      }

      start() {
        this.wanted = true;
        /* ⚠️ RE-RUN THE FEATHER HERE, BECAUSE LAYOUT MAY NOT HAVE EXISTED YET.
           applyFeather() needs `getBoundingClientRect().width`, and that is 0 for a
           card in a container that is `display:none` or in a background tab — the
           diamond/colour tab switch on the browse page builds a whole grid that
           way. Its other two call sites are connect and `loadedmetadata`, both of
           which can land while the card is hidden, and nothing re-armed them: the
           feather would then be silently absent for the life of the page, which is
           indistinguishable from "this stone has no margin". Observed with the
           whole document hidden (innerWidth 0) — 0 of 30 cards feathered.
           A hovered card is laid out by definition, so this is the one call site
           that cannot be starved. It is idempotent and costs a few multiplications,
           and it also picks up a tile that changed size on a viewport resize. */
        this.applyFeather();
        this.arm();
        stopCurrent(this);
        current = this;
        // ⚠️ A WARMED CLIP HAS ALREADY FIRED `loadeddata`, AND IT WILL NOT FIRE IT
        // AGAIN. Now that reveal() refuses to run unless armed, the reveal for a
        // warmed card can only come from `playing` — which is one more round trip,
        // and nothing at all if a browser ever refuses the play(). The frame is
        // already decoded and sitting there, so read readyState directly instead of
        // waiting for an event that has been and gone. This is also what makes the
        // first hover instant, which is the entire reason for warming.
        if (this.video.readyState >= 2) this.reveal();
        if (!this.classList.contains('is-playing')) this.classList.add('is-loading');
        clearTimeout(this.stallTimer);
        this.stallTimer = setTimeout(() => {
          if (!this.classList.contains('is-playing')) this.giveUp();
        }, STALL_MS);
        var p = this.video.play();
        if (p && p.catch) p.catch(() => { /* reveal() may still fire on loadeddata */ });
      }

      stop() {
        this.wanted = false;
        clearTimeout(this.timer);
        clearTimeout(this.stallTimer);
        this.classList.remove('is-loading', 'is-playing');
        if (this.armed) this.video.pause();
      }

      onEnter = (e) => {
        // A tap on a touch screen also fires pointerenter; it must not start a
        // multi-megabyte download on the connection least able to afford it.
        if (e.pointerType && e.pointerType !== 'mouse') return;
        if (reduceMotion || saveData) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.start(), HOVER_DELAY);
      };

      onLeave = () => {
        if (this.dragging) return;         // never yank it away mid-scrub
        clearTimeout(this.timer);
        this.stop();
        if (current === this) current = null;
      };

      /* ------------------------------------------------------------ drag ---- */
      onDown = (e) => {
        // FIRST, and before every early return: a `moved` left true by a gesture
        // that ended in `pointercancel` is never cleared by onClick, because no
        // click follows a cancel. It would then make the NEXT honest click
        // preventDefault itself into nothing — one more intermittent dead press.
        this.moved = false;
        if (e.target === this.badge || this.badge.contains(e.target)) return;
        // ⚠️ SCRUBBING IS MOUSE-ONLY, for the same reason hover is. The header's
        // bargain is that press-and-hold is only reasonable BECAUSE hover warmed
        // the buffer first — and a phone never hovers, so it never earns the drag.
        // Without this a touch card that had been armed by its badge would read a
        // diagonal SCROLL as a scrub: 10px of horizontal drift takes the pointer
        // capture and preventDefaults the moves, which hijacks the page scroll and
        // eats the tap. On touch the badge is the whole interaction (see
        // snippets/card-stone-spin.liquid), so a tap here is only ever a tap, and
        // a tap must reach the <a>.
        if (e.pointerType && e.pointerType !== 'mouse') return;
        if (!this.armed || !this.video.duration) return;   // nothing loaded to scrub
        this.dragging = true;
        this.captured = false;
        this.pressX = e.clientX;
        // NO pause and NO capture here — see the header. Until the pointer has
        // travelled DRAG_SLOP this is still a click, and a click must reach the <a>.
      };

      onMove = (e) => {
        if (!this.dragging) return;
        if (!this.moved) {
          if (Math.abs(e.clientX - this.pressX) < DRAG_SLOP) return;  // still a click
          // The gesture has become a drag. Take the clip, and only NOW the pointer.
          this.moved = true;
          this.video.pause();                              // hand over control
          this.classList.add('is-dragging');
          try { this.host.setPointerCapture(e.pointerId); this.captured = true; } catch (_) {}
          // Re-baseline both. The clip kept PLAYING through the press, so a
          // currentTime sampled at pointerdown is stale by however long the finger
          // rested, and the slop travel is not rotation the shopper asked for.
          // Baselining here is what stops the stone jumping as the drag takes hold.
          this.startX = e.clientX;
          this.startT = this.video.currentTime;
        }
        e.preventDefault();
        var d = this.video.duration;
        // One card-width of travel is one full rotation. SUBTRACTED — see header.
        var frac = (e.clientX - this.startX) / Math.max(1, this.host.clientWidth);
        var t = (this.startT - frac * d) % d;
        if (t < 0) t += d;
        this.video.currentTime = t;
      };

      onUp = (e) => {
        if (!this.dragging) return;
        this.dragging = false;
        this.classList.remove('is-dragging');
        // Only release what was actually taken — capture is now conditional.
        if (this.captured) {
          this.captured = false;
          try { this.host.releasePointerCapture(e.pointerId); } catch (_) {}
        }
        // Resume only if the cursor is still over the card; a drag that ended
        // off-card should leave it where the shopper put it. A press that never
        // became a drag never paused, so there is nothing to resume.
        if (this.moved && this.host.matches(':hover')) this.video.play().catch(() => {});
      };

      onClick = (e) => {
        // A drag ends in a click the browser would follow to the product page.
        if (this.moved) {
          e.preventDefault();
          e.stopPropagation();
          this.moved = false;
        }
      };

      onBadge = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.classList.contains('is-dead')) return;
        if (this.video.paused) this.start();
        else { this.stop(); if (current === this) current = null; }
      };
    });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stopCurrent(null);
  });
})();
